import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiTokenSummary } from "@travel/shared";
import { api, ApiError } from "../../api/client";
import { BottomSheet } from "../common/BottomSheet";
import { Button } from "../common/Button";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ApiTokensDialog({ open, onClose }: Props) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["tokens"],
    queryFn: () => api.tokens.list().then((r) => r.tokens),
    enabled: open,
  });

  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Holds the plaintext token returned from a successful create call. Shown
  // exactly once to the user — after the dialog is dismissed it's gone.
  const [newToken, setNewToken] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiTokenSummary | null>(null);

  const createMutation = useMutation({
    mutationFn: (label: string) => api.tokens.create(label),
    onSuccess: (res) => {
      setNewToken(res.token);
      setLabel("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["tokens"] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : "토큰 발급 실패");
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.tokens.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tokens"] }),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    createMutation.mutate(label.trim());
  }

  async function copyToken() {
    if (!newToken) return;
    try {
      await navigator.clipboard.writeText(newToken);
    } catch {
      // some browsers (HTTP non-localhost) block clipboard. Fall back: select.
    }
  }

  return (
    <>
      <BottomSheet
        open={open && !revokeTarget}
        onClose={() => {
          setNewToken(null);
          onClose();
        }}
        title="MCP 토큰"
      >
        <div className="px-4 pb-4 space-y-4">
          {newToken ? (
            <div className="space-y-2">
              <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
                토큰은 이번 한 번만 표시됩니다. 닫기 전에 복사해두세요.
              </p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={newToken}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 px-3 py-2.5 border border-slate-300 rounded-xl font-mono text-xs bg-slate-50 min-h-[44px]"
                />
                <Button onClick={copyToken}>복사</Button>
              </div>
              <Button variant="secondary" className="w-full" onClick={() => setNewToken(null)}>
                완료
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-2">
              <label className="block">
                <span className="text-sm text-slate-600 mb-1 block">새 토큰 라벨 (선택)</span>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={60}
                    placeholder="Claude Desktop"
                    className="flex-1 px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
                  />
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? "발급 중…" : "발급"}
                  </Button>
                </div>
              </label>
              {error && (
                <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
                  {error}
                </pre>
              )}
              <p className="text-xs text-slate-500">
                Claude의 MCP 서버 URL: <code className="font-mono">/mcp</code> · Bearer 토큰으로 인증.
              </p>
            </form>
          )}

          <div>
            <h3 className="text-sm text-slate-600 mb-2">발급된 토큰 ({data?.length ?? 0})</h3>
            {isLoading ? (
              <p className="text-slate-500 text-sm">불러오는 중…</p>
            ) : data && data.length > 0 ? (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                {data.map((t) => (
                  <li key={t.id} className="px-3 py-2.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {t.label || "(라벨 없음)"}
                      </div>
                      <div className="text-xs text-slate-500 font-mono truncate">
                        {t.prefix}…
                      </div>
                      <div className="text-[11px] text-slate-400">
                        발급 {new Date(t.createdAt).toLocaleDateString("ko-KR")}
                        {t.lastUsedAt &&
                          ` · 마지막 사용 ${new Date(t.lastUsedAt).toLocaleDateString("ko-KR")}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRevokeTarget(t)}
                      className="text-xs text-rose-600 hover:text-rose-700 px-2 py-1 rounded-lg min-h-[36px]"
                    >
                      회수
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm">아직 발급된 토큰이 없습니다.</p>
            )}
          </div>
        </div>
      </BottomSheet>

      <ConfirmDialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (revokeTarget) revokeMutation.mutate(revokeTarget.id);
          setRevokeTarget(null);
        }}
        title="이 토큰을 회수할까요?"
        message={`회수 후에는 이 토큰으로 더 이상 접근할 수 없습니다.${
          revokeTarget?.label ? `\n라벨: ${revokeTarget.label}` : ""
        }`}
        confirmLabel="회수"
        destructive
      />
    </>
  );
}
